import classNames from 'classnames';
import React, {
  ChangeEventHandler,
  CSSProperties,
  FocusEventHandler,
  forwardRef,
  ReactNode,
} from 'react';

export type InputType =
  | 'text'
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'url';

export interface InputProps {
  /**
   * Defines an add-on element, usually an icon to be displayed in the component.
   */
  addOn?: ReactNode;
  /**
   * The ID of the DOM element that describes this input element.
   * Should be used for accessibility linking.
   */
  'aria-describedby'?: string | undefined;
  /**
   * The ID of the DOM element that shows an error message for this input.
   * Should be used for accessibility linking.
   */
  'aria-errormessage'?: string | undefined;
  /**
   * Whether or not the input contains an invalid value.
   * Should be used for accessibility, but this also affects the visual look
   * of the input field!
   */
  'aria-invalid'?: boolean | undefined;
  /**
   * If there is no <label> element associated with this input, then you
   * must provide this attribute to provide the input a label.
   */
  'aria-label'?: string | undefined;
  /**
   * Optional class name to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  className?: string;
  /**
   * The initial string value shown in the input field (for uncontrolled state management).
   */
  defaultValue?: string;
  /**
   * Whether or not the input field is disabeld.
   */
  disabled?: boolean;
  /**
   * The ID for the input element. It is added to the input element
   * and not to the container. Should be used for accessibility linking.
   */
  id?: string;
  /**
   * Maximum allowed numeric value of the input with HTML5 validation.
   */
  max?: number | string | undefined;
  /**
   * Maximum allowed length of the input with HTML5 validation.
   */
  maxLength?: number;
  /**
   * Minimum allowed numeric value of the input with HTML5 validation.
   */
  min?: number | string | undefined;
  /**
   * Minimum allowed length of the input with HTML5 validation.
   */
  minLength?: number;
  /**
   * A name of the form input element.
   */
  name?: string;
  /**
   * The event handler when the input field looses focus.
   */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * The event handler when the value on the input field changes.
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
   * The event handler when the input field is focused.
   */
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /**
   * Required regexp pattern for valid values with HTML5 validation.
   */
  pattern?: string;
  /**
   * Placeholder text to show when the value is empty.
   */
  placeholder?: string;
  /**
   * Whether or not the input field is read only and cannot be edited.
   */
  readOnly?: boolean;
  /**
   * Whether or not to make the input required with HTML5 validation.
   */
  required?: boolean;
  /**
   * Optional inline styles to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  style?: CSSProperties;
  /**
   * Type of the input field. Only text input types are allowed.
   * Defaults to `text`.
   */
  type?: InputType;
  /**
   * String value shown in the input field (for controlled state management).
   */
  value?: string;
}
/**
 * This is a low-level component used to render a UI5 input component styled correctly.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    'aria-describedby': ariaDescribedBy,
    'aria-errormessage': ariaErrorMessage,
    'aria-invalid': ariaInvalid,
    'aria-label': ariaLabel,
    addOn,
    className,
    defaultValue,
    disabled,
    id,
    max,
    maxLength,
    min,
    minLength,
    name,
    onBlur,
    onChange,
    onFocus,
    pattern,
    placeholder,
    readOnly,
    required,
    style,
    type = 'text',
    value,
  } = props;

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (disabled) {
      // Prevent calling onChange if the input is disabled
      return;
    }

    onChange?.(event);
  };

  return (
    <div
      className={classNames(
        className,
        'fd-input-group',
        disabled && 'is-disabled',
        ariaInvalid && 'is-error',
      )}
    >
      <input
        aria-describedby={ariaDescribedBy}
        aria-errormessage={ariaErrorMessage}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        className="fd-input fd-input-group__input"
        defaultValue={defaultValue}
        disabled={disabled}
        id={id}
        max={max}
        maxLength={maxLength}
        min={min}
        minLength={minLength}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        pattern={pattern}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={ref}
        required={required}
        style={style}
        type={type}
        value={value}
      />
      {addOn && <span className="fd-input-group__addon">{addOn}</span>}
    </div>
  );
});
